import compatibility from '../backend/client-compatibility.json';

export const CLIENT_VERSION = compatibility.minimumVersion;
export const CLIENT_VERSION_HEADER = 'X-Mors-Client-Version';
/** Edge ставит `X-Mors-Maintenance: 1` на ответ страницы техработ. */
export const MAINTENANCE_HEADER = 'X-Mors-Maintenance';

export interface AppVersion {
  version?: string;
  buildTime?: string;
  gitSha?: string;
  minimumClientVersion?: number;
  force?: boolean;
}

export type AppUpdateState = 'ready' | 'updating' | 'failed' | 'maintenance';
/** actionLost: техработы отклонили действие пользователя, оно не выполнено. */
type AppUpdateListener = (state: AppUpdateState, actionLost: boolean) => void;
const MAINTENANCE_PROBE_INTERVAL_MS = 10_000;
let state: AppUpdateState = 'ready';
let requiredVersion = 0;
let checking: Promise<AppVersion | null> | null = null;
let maintenanceActionLost = false;
let maintenanceProbing = false;
const listeners = new Set<AppUpdateListener>();

const setState = (next: AppUpdateState): void => {
  state = next;
  listeners.forEach((listener) => listener(next, maintenanceActionLost));
};

export function onAppUpdate(listener: AppUpdateListener): () => void {
  listeners.add(listener);
  listener(state, maintenanceActionLost);
  return () => { listeners.delete(listener); };
}

/**
 * Ответ с `MAINTENANCE_HEADER` отдаёт edge в режиме техработ: запрос не дошёл до приложения,
 * ничего не записано. Блокируем UI вместо тостов и ждём, пока `/version.json` снова ответит; страницу не
 * перезагружаем, чтобы не потерять ввод. Новую сборку покажет обычная проверка версии.
 */
export function reportMaintenance(actionLost = false, fetcher: typeof fetch = fetch): void {
  if (state === 'updating' || state === 'failed') return;
  maintenanceActionLost ||= actionLost;
  setState('maintenance');
  if (maintenanceProbing) return;
  maintenanceProbing = true;
  const probe = async (): Promise<void> => {
    try {
      const response = await fetcher('/version.json', { cache: 'no-store', signal: AbortSignal.timeout(5_000) });
      if (response.ok) {
        maintenanceProbing = false;
        maintenanceActionLost = false;
        if (state === 'maintenance') setState('ready');
        return;
      }
    } catch {
      // Сервис ещё недоступен: следующая проверка ниже.
    }
    setTimeout(() => void probe(), MAINTENANCE_PROBE_INTERVAL_MS);
  };
  void probe();
}

export function needsMandatoryUpdate(remote: AppVersion, currentVersion = CLIENT_VERSION): boolean {
  return Number.isSafeInteger(remote.minimumClientVersion) && (remote.minimumClientVersion ?? 0) > currentVersion;
}

/** Сначала убеждаемся, что свежие assets доступны; 503/offline не вызывают reload. */
export async function checkAppVersion(fetcher: typeof fetch = fetch): Promise<AppVersion | null> {
  if (checking) return checking;
  checking = (async () => {
    try {
      const response = await fetcher('/version.json', { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
      if (!response.ok) return null;
      const remote = await response.json() as AppVersion;
      if (typeof remote?.buildTime !== 'string') return null;
      if (needsMandatoryUpdate(remote)) {
        requiredVersion = Math.max(requiredVersion, remote.minimumClientVersion ?? 0);
        setState('updating');
      }
      if (requiredVersion > CLIENT_VERSION && (remote.minimumClientVersion ?? 0) >= requiredVersion) {
        // При ошибке раздачи assets не загоняем пользователя в бесконечные reload.
        const key = 'mors.app-update.reload';
        const target = `${requiredVersion}:${remote.buildTime}`;
        try {
          if (sessionStorage.getItem(key) === target) {
            setState('failed');
            return remote;
          }
          sessionStorage.setItem(key, target);
        } catch {
          // Без доступного sessionStorage автоматическая перезагрузка небезопасна для UX.
          setState('failed');
          return remote;
        }
        window.location.reload();
      }
      return remote;
    } catch {
      return null;
    }
  })().finally(() => { checking = null; });
  return checking;
}

export function requireAppUpdate(minimumVersion: unknown): void {
  if (typeof minimumVersion !== 'number' || !Number.isSafeInteger(minimumVersion) || minimumVersion <= CLIENT_VERSION) {
    setState('failed');
    return;
  }
  requiredVersion = Math.max(requiredVersion, minimumVersion);
  setState('updating');
  void checkAppVersion();
}

export function startAppVersionPolling(onVersion?: (remote: AppVersion) => void): () => void {
  // checkAppVersion дедупит параллельные запросы, поэтому focus+visibilitychange не дают двойной fetch.
  const check = (): void => {
    if (document.visibilityState !== 'visible') return;
    void checkAppVersion().then((remote) => {
      if (remote) onVersion?.(remote);
    });
  };
  check();
  const interval = window.setInterval(check, 60_000);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  return () => {
    clearInterval(interval);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
  };
}

// Новый service worker может определить версию даже у уже открытых вкладок.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'mors:client-version') event.ports[0]?.postMessage({ version: CLIENT_VERSION });
  });
}
